import * as React from "react"
import Upload from "rc-upload"

import { Modal } from "@tenforce/toolbox-modal-react"
import { Action, ActionType } from "@tenforce/toolbox-button-push-react"

import { Symbols } from "@tenforce/toolbox-fontmap"
import { formatString } from "../../../util/Strings"
import bem from "../../../bem"

import AttachmentCard from "./AttachmentCard"
import Lightbox from "./Lightbox"
import { Labels } from "../../../util/Translations"
import { IAttachment, ILightbox } from "../index"

export const className = "toolbox-attachment"
const b = bem.with(className)

interface OwnProps {
    fieldName: string
    customFieldDefinitionId: string
    canAddAttachments: boolean
    canEditAttachments: boolean
    canDownloadAttachments: boolean

    maxInitialCards: number

    attachments: IAttachment[]
    attachmentsWithoutPagination?: IAttachment[]
    showAllDocuments: boolean
    attachmentLoadCounter: number
    onAddAttachment: (request: UploadRequest) => void
    onDeleteAttachment: (key: number) => void
    onUpdateAttachmentDescription: (key: number, value: string) => void
    loadMoreAttachments?: () => void
    onAttachmentLoad?: () => void

    showLightbox: (lightboxStartingIndex: number) => void
    hideLightbox: () => void
    changeAttachmentInLightbox: (diff: number) => void
    lightbox: ILightbox

    onMouseEnterBrowseDrop?: (event: React.MouseEvent) => void
    onMouseOutBrowseDrop?: (event: React.MouseEvent) => void

    wrapperClasses: string
    uploadButtonClasses: string
}

type Props = OwnProps

interface State {
    isDragging: boolean
    lightboxStartingIndex: number
}

export default class InlineAttachment extends React.PureComponent<
    Props,
    State
> {
    static defaultProps: Partial<Props> = {
        canDownloadAttachments: true,
        wrapperClasses: "defaultCardSettings",
        uploadButtonClasses: "",
    }

    constructor(props: Props) {
        super(props)
        this.state = {
            isDragging: false,
            lightboxStartingIndex: 0,
        }
    }

    private dragRef = React.createRef<HTMLDivElement>()
    private counter = 0

    render(): JSX.Element {
        const {
            attachmentLoadCounter,
            attachmentsWithoutPagination,
            canAddAttachments,
            canEditAttachments,
            canDownloadAttachments,
            customFieldDefinitionId,
            fieldName,
            hideLightbox,
            changeAttachmentInLightbox,
            maxInitialCards,
            lightbox,
            onAddAttachment,
            onAttachmentLoad,
            onDeleteAttachment,
            onUpdateAttachmentDescription,
            showAllDocuments,
            showLightbox,
            uploadButtonClasses,
            wrapperClasses,
        } = this.props
        const { isDragging } = this.state
        const { isShown: isLightboxShown } = lightbox

        let { attachments } = this.props

        let nMoreToShow = 0
        if (!showAllDocuments) {
            if (attachments.length > maxInitialCards) {
                nMoreToShow = attachments.length - (maxInitialCards - 1)
                attachments = attachments.slice(0, maxInitialCards - 1)
            }
        }

        const { DropItAs, LoadNMore, BrowseDropFilesHere } = Labels
        return (
            <>
                {isLightboxShown && (
                    <Lightbox
                        lightbox={lightbox}
                        customFieldDefinitionId={customFieldDefinitionId}
                        attachments={
                            attachmentsWithoutPagination ?? attachments
                        }
                        onAttachmentLoad={onAttachmentLoad}
                        onDeleteAttachment={onDeleteAttachment}
                        onUpdateAttachmentDescription={
                            onUpdateAttachmentDescription
                        }
                        hideLightbox={hideLightbox}
                        changeAttachmentInLightbox={changeAttachmentInLightbox}
                        canEditAttachments={canEditAttachments}
                        canDownloadAttachments={canDownloadAttachments}
                    />
                )}

                <Modal
                    isShown={canAddAttachments && isDragging}
                    containerProps={{
                        isCenterFixedPositioned: true,
                        blockInteraction: true,
                    }}
                >
                    <div className={`${b("dropItModal")} is-inverted`}>
                        <h2>
                            {Symbols.Pin} {DropItAs} {fieldName}
                        </h2>
                    </div>
                </Modal>

                <div
                    className={b("attachmentsContainer")}
                    onDragEnter={this.onDragStart}
                    onDragLeave={this.onDragStop}
                    onDrop={this.onDragStop}
                    ref={this.dragRef}
                >
                    {canAddAttachments && (
                        <Upload
                            customRequest={onAddAttachment}
                            multiple={true}
                            component="div"
                            className="selectUpload"
                            disabled={!canAddAttachments}
                        >
                            <Action
                                actionType={ActionType.action}
                                data-ft-add
                                // this is not a parameter as the focused design is added on :focus event
                                // but here we are forcing this styling as the button is not in focus
                                extraClasses={`${uploadButtonClasses} ${
                                    isDragging ? "is-focused" : ""
                                }`}
                                onMouseEnter={this.onMouseEnterBrowseDrop}
                                onMouseOut={this.onMouseOutBrowseDrop}
                            >
                                {Symbols.Attachment}&nbsp;
                                {BrowseDropFilesHere}
                            </Action>
                        </Upload>
                    )}

                    <Upload
                        customRequest={onAddAttachment}
                        multiple={true}
                        component="div"
                        className={
                            "clickUpload" + (isDragging ? "" : " is-hidden")
                        }
                        disabled={!canAddAttachments}
                        openFileDialogOnClick={false}
                    />

                    <div
                        className={b("attachmentsGrid")}
                        data-ft-all-visible={showAllDocuments}
                        data-ft-all-loaded={attachmentLoadCounter === 0}
                    >
                        {attachments.map((attachment, index) => {
                            return (
                                <AttachmentCard
                                    attachment={attachment}
                                    wrapperClasses={wrapperClasses}
                                    onAttachmentLoad={onAttachmentLoad}
                                    showLightbox={showLightbox}
                                    key={attachment.key}
                                    index={index}
                                />
                            )
                        })}

                        {!showAllDocuments && nMoreToShow > 0 && (
                            <div className={wrapperClasses}>
                                <div className={b("loadMore")}>
                                    <div className="absolute-container">
                                        <Action
                                            actionType={ActionType.action}
                                            extraClasses="full-size"
                                            onClick={(
                                                event: React.MouseEvent,
                                            ): void => {
                                                event.preventDefault()
                                                this.onShowMore()
                                            }}
                                        >
                                            <div className="h3">
                                                {Symbols.Cards}
                                            </div>
                                            <p className="label">
                                                {formatString(
                                                    LoadNMore,
                                                    nMoreToShow,
                                                )}
                                            </p>
                                        </Action>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </>
        )
    }

    onDragStart = (): void => {
        if (this.counter === 0) {
            this.setState({ isDragging: true })
        }
        this.counter++
    }

    onDragStop = (): void => {
        this.counter--
        if (this.counter === 0) {
            this.setState({ isDragging: false })
        }
    }

    onShowMore = (): void => {
        const { loadMoreAttachments, showAllDocuments } = this.props
        if (!showAllDocuments && !!loadMoreAttachments) {
            loadMoreAttachments()
        }
    }

    onMouseEnterBrowseDrop = (event: React.MouseEvent): void => {
        const { onMouseEnterBrowseDrop } = this.props
        if (!!onMouseEnterBrowseDrop) {
            onMouseEnterBrowseDrop(event)
        }
    }

    onMouseOutBrowseDrop = (event: React.MouseEvent): void => {
        const { onMouseOutBrowseDrop } = this.props
        if (!!onMouseOutBrowseDrop) {
            onMouseOutBrowseDrop(event)
        }
    }
}
